import { ProductInfo } from './ProductInfo';
import { SupplementKnowledgeDetailResponse } from 'services/api/supplementKnowledge';
import { ChatConversationProvider } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import { ChatErrorBoundary } from 'views/Conversation/components/ChatSection';
import Chat from 'views/Conversation/Chat';
import { useMemo, useState } from 'react';
import { css } from '@emotion/css';
import { MenuUnfoldOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useModifyDrawerContext } from '../context/ModifyDrawerContext';

interface SupplementKnowledgeChatPanelProps {
  dataSource: SupplementKnowledgeDetailResponse;
  onRefresh?: () => void;
  onDelayRefresh?: () => void;
  fold?: boolean;
  onFold?: (fold: boolean) => void;
  showExpandSkuButton?: boolean;
}

export const SupplementKnowledgeChatPanel = (props: SupplementKnowledgeChatPanelProps) => {
  const { dataSource, onRefresh, onDelayRefresh, showExpandSkuButton } = props;
  const conversationId = `${dataSource.conversationId}`;
  const [fold, setFold] = useState(false);
  const { t } = useTranslate();

  const { primaryKey } = useModifyDrawerContext();

  const highlightMsg = useMemo(() => {
    const { answers, question } = dataSource.supplementalKnowledgeValue;
    const highlightMsg: Record<string, string> = {};
    answers.forEach((answer) => {
      if (answer.messageId) {
        highlightMsg[answer.messageId] = answerBgCss;
      }
    });
    if (question?.messageId) {
      highlightMsg[question.messageId] = questionBgCss;
    }
    return highlightMsg;
  }, [dataSource]);

  if (fold) {
    return (
      <div className="h-full bg-[#FFFFFF] pl-[6px] py-[6px]">
        <Tooltip title={t('将聊天记录展开')}>
          <div className="cursor-pointer hover:opacity-80" onClick={() => setFold(false)}>
            <MenuUnfoldOutlined />
          </div>
        </Tooltip>
      </div>
    );
  }

  const { id, itemIdentifier, supplementalKnowledgeValue, expandCount, storeInfo } = dataSource;
  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden border-t-0 border-r-0 border-b-0 border-r border-solid border-[#F0F2F5]">
      <ProductInfo
        knowledgeId={id}
        submitItemIdentifier={itemIdentifier}
        skuIds={supplementalKnowledgeValue.skuIds}
        dataSource={{
          expandCount: expandCount,
          storeInfo: storeInfo,
        }}
        onRefresh={onRefresh}
        onDelayRefresh={onDelayRefresh}
        onFold={() => setFold(true)}
        showExpandSkuButton={showExpandSkuButton}
      />
      <div className="flex-1">
        <ChatConversationProvider conversationId={conversationId}>
          <ChatErrorBoundary>
            <Chat
              conversationId={conversationId}
              isGalaxy
              highlightMsg={highlightMsg}
              primaryKey={primaryKey}
            />
          </ChatErrorBoundary>
        </ChatConversationProvider>
      </div>
    </div>
  );
};

const answerBgCss = css`
  background-color: rgba(0, 0, 0, 0.06);

  &.flash-animation {
    animation: answer-flash 2s ease-in-out infinite;

    @keyframes answer-flash {
      0% {
        background-color: rgba(0, 0, 0, 0.06);
      }
      50% {
        background-color: rgba(0, 0, 0, 0.3);
      }
      100% {
        background-color: rgba(0, 0, 0, 0.06);
      }
    }
  }
`;

const questionBgCss = css`
  background-color: rgba(48, 255, 238, 0.11);

  &.flash-animation {
    animation: question-flash 2s ease-in-out infinite;

    @keyframes question-flash {
      0% {
        background-color: rgba(48, 255, 238, 0.1);
      }
      50% {
        background-color: rgba(48, 255, 238, 0.5);
      }
      100% {
        background-color: rgba(48, 255, 238, 0.1);
      }
    }
  }
`;
